import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig'; 
import '@/App.css';

// === PÁGINAS ===
import PaginaInicio from '@/pages/PaginaInicio';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Vendas from '@/pages/Vendas';
import NovaVenda from '@/pages/NovaVenda';
import EditarVenda from '@/pages/EditarVenda';
import Pessoas from '@/pages/Pessoas'; // Pode não ser usado se tiver rotas específicas abaixo
import Faturamento from '@/pages/Faturamento';
import Perfil from '@/pages/Perfil';
import DefinirSenha from './pages/DefinirSenha';
import VerifyEmail from './pages/VerifyEmail';
import RelatoriosRankings from '@/pages/Relatorios'; 
import RelatoriosEvolucao from '@/pages/RelatoriosEvolucao'; 
import GerenciadorVoos from '@/pages/GerenciadorVoos'; 
import FluxoCaixaView from '@/pages/Financeiro/FluxoCaixaView';
import ContasAReceberView from '@/pages/Financeiro/ContasAReceberView';
import ContasAPagarView from '@/pages/Financeiro/ContasAPagarView';
import NovoRecebimentoView from '@/pages/Financeiro/NovoRecebimentoView';
import NovoPagamentoView from '@/pages/Financeiro/NovoPagamentoView';
import CadastroClientePublico from '@/pages/CadastroClientePublico';
import Termos from '@/pages/Termos';
import Auditoria from '@/pages/Auditoria';
import SuperAdmin from '@/pages/SuperAdmin';
import Fornecedores from '@/pages/Pessoas/Fornecedores';
import Colaboradores from '@/pages/Pessoas/Colaboradores';
import Clientes from '@/pages/Pessoas/Clientes';
import Contas from '@/pages/Pessoas/ContasBancarias';

// === COMPONENTES ===
import { Toaster } from '@/components/ui/sonner';
import Sidebar from '@/components/Sidebar';
import Footer from '@/components/Footer'; // <--- IMPORTANTE: Import do Footer
import SubscriptionBlocked from '@/components/SubscriptionBlocked'; 
import { Loader2 } from 'lucide-react'; 

function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const verifySession = async () => {
      try {
        // Tenta buscar o perfil do usuário via Cookie
        const res = await axiosInstance.get('/auth/me');
        
        // --- AQUI ESTÁ A CORREÇÃO ---
        // Se deu certo, RESTAURA o usuário no localStorage para o UserWidget ver
        if (res.data) {
          localStorage.setItem('user', JSON.stringify(res.data));
        }
        // ----------------------------
        
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Sessão inválida ou expirada", error);
        localStorage.removeItem('user'); // Limpa lixo se falhar
        setIsAuthenticated(false);
      }
    };
    verifySession();
  }, []);

  if (isAuthenticated === null) {
    return <div className="flex h-screen items-center justify-center">Carregando...</div>; 
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

// === LAYOUT INTELIGENTE COM BLOQUEIO VISUAL ===
function AppLayout() {
  const [status, setStatus] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const location = useLocation();

  useEffect(() => {
    checkSubscription();
  }, [location.pathname]); 

  const checkSubscription = async () => {
    try {
      
      const res = await axiosInstance.get('/assinatura/status');
      setStatus(res.data.status);
      setSubscriptionData(res.data);
    } catch (error) {
      console.error("Erro ao verificar assinatura", error);
      if (error.response?.status === 402) {
        setStatus('INADIMPLENTE');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- ESTADO: CARREGANDO ---
  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 overflow-hidden">
        <div className="flex-shrink-0 h-full">
            <Sidebar />
        </div>
        <main className="flex-1 overflow-y-auto overflow-hidden flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  // --- ESTADO: BLOQUEIO "FANTASMA" (Pagamento Pendente) ---
  // Se estiver bloqueado E não estiver na página de Perfil (pagamento)
  if (
    (status === 'INADIMPLENTE' || status === 'CANCELADA' || status === 'BLOQUEADA')
    && !location.pathname.startsWith('/perfil')) {
    return (
      <div className="flex h-screen bg-gray-100 overflow-hidden">
        
        {/* SIDEBAR "FANTASMA": Visível, mas desabilitada */}
        <div className="flex-shrink-0 h-full overflow-hidden pointer-events-none opacity-50 filter grayscale select-none" aria-hidden="true">
            <Sidebar />
        </div>
        
        {/* ÁREA PRINCIPAL: Mensagem de bloqueio */}
        <main className="flex-1 overflow-y-auto relative flex items-center justify-center bg-gray-50/50">
             <div className="overflow-hidden absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]"></div>
             <div className="w-full max-w-5xl z-10 p-4">
                 <div className="bg-white shadow-2xl rounded-xl border border-gray-200 overflow-hidden">
                    <SubscriptionBlocked dados={subscriptionData} />
                 </div>
             </div>
        </main>
      </div>
    );
  }

  // === LAYOUT NORMAL (LOGADO E ATIVO) ===
  return (
    // FIX 1: 'overflow-hidden' na raiz previne scroll duplo na janela
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      
      {/* FIX 2: Wrapper com 'flex-shrink-0' e 'h-full' garante que a Sidebar não encolha */}
      <div className="flex-shrink-0 h-full">
        <Sidebar />
      </div>

      {/* FIX 3: 'flex flex-col' permite organizar Conteúdo em cima e Footer embaixo */}
      <main className="flex-1 overflow-y-auto relative flex flex-col">
        
        {/* Wrapper do conteúdo da página (Cresce com flex-1 para empurrar o footer) */}
        <div className="flex-1 p-6">
            <Outlet /> 
        </div>

        {/* Rodapé fixado ao final do fluxo de conteúdo */}
        <Footer /> 
        
      </main>
    </div>
  );
}

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* === ROTAS PÚBLICAS === */}
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro-cliente/:id" element={<CadastroClientePublico />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/definir-senha" element={<DefinirSenha />} />
          <Route path="/termos" element={<Termos />} />

          {/* === ROTAS PRIVADAS === */}
          <Route 
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          >
            <Route path="/" element={<PaginaInicio/>} />
            
            {/* Vendas */}
            <Route path="/vendas" element={<Vendas />} />
            <Route path="/vendas/nova" element={<NovaVenda />} />
            <Route path="/vendas/editar/:id" element={<EditarVenda />} />
            
            {/* Operacional */}
            <Route path="/voos" element={<GerenciadorVoos />} />
            
            {/* Pessoas / Cadastros */}
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/pessoas/fornecedores" element={<Fornecedores />} />
            <Route path="/pessoas/colaboradores" element={<Colaboradores />} />
            <Route path="/pessoas/contas" element={<Contas />} />

            {/* Relatórios */}
            <Route path="/relatorios/rankings" element={<RelatoriosRankings />} />
            <Route path="/relatorios/evolucao" element={<RelatoriosEvolucao />} />
            <Route path="/relatorios" element={<Navigate to="/relatorios/rankings" replace />} />
            
            {/* Financeiro */}
            <Route path="/financeiro/dre" element={<Faturamento />} />
            <Route path="/financeiro/fluxo-caixa" element={<FluxoCaixaView />} />
            <Route path="/financeiro/contas-a-receber" element={<ContasAReceberView />} />
            <Route path="/financeiro/contas-a-pagar" element={<ContasAPagarView />} />
            <Route path="/financeiro/novo-recebimento" element={<NovoRecebimentoView />} />
            <Route path="/financeiro/novo-pagamento" element={<NovoPagamentoView />} />

            {/* Redirecionamentos de Financeiro */}
            <Route path="/financeiro" element={<Navigate to="/financeiro/fluxo-caixa" replace />} />
            <Route path="/faturamento" element={<Navigate to="/financeiro/dre" replace />} />
            
            {/* Configurações / Admin */}
            <Route path="/perfil" element={<Perfil />} />
            <Route path="/auditoria" element={<Auditoria />} />
            <Route path="/admin-god-mode" element={<SuperAdmin />} />
          </Route>
          
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;